import React from 'react';
import { Navigate } from 'react-router-dom';
import useAuth from '../../pages/Personal Attendance/useAuth';

/**
 * Role / permission / module-gated route wrapper.
 *
 * Existing usage (UNCHANGED — these props still work):
 *   <RoleProtected requireItAdmin>            <AdminPage /></RoleProtected>
 *   <RoleProtected requireHr>                 <HrPage /></RoleProtected>
 *   <RoleProtected requireManager>            <ManagerPage /></RoleProtected>
 *   <RoleProtected requireSuperUser>          <KpiPage /></RoleProtected>
 *   <RoleProtected requireAny={['IT Admin','HR Admin']}><Page /></RoleProtected>
 *
 * Phase 3 — new props for legacy-rule parity:
 *
 *   requirePermission="HR Menu"
 *     Matches PermissionName in SSW_Dashboard_Permissions, case-insensitive.
 *     Equivalent to legacy hasAccess("HR Menu").
 *
 *   requireModule="epo.accounting"
 *     Reads the server-resolved Modules map. The list of keys is in
 *     EffectiveAccessService — "dept.hr", "epo.accounting", "epo.mpc",
 *     "epo.receive", "dms.release", "dms.manager", "hr.menu", "task.auditor",
 *     "app.mafia", "app.di", "app.displayboard", "admin.permissions", etc.
 *
 *   requireCountry="INDIA" | "USA"
 *     Gate visibility of country-specific pages. Reads the bundle's
 *     canViewIndia / canViewUsa flags so super-users and IT admin can
 *     still toggle across countries (legacy IndiaOverride behavior).
 *
 *   requireFlag="isSalvie" | "isBabu" | "isMPC" | "isShipper" |
 *               "isTaskAuditor" | "isMaintenanceTeam" | "isBetaTester" |
 *               "isSnSignatory" | "isFinance" | "isDmsManager" |
 *               "isProjectApprovalCommittee" | "isFlOperations"
 *     Any of the resolved access-bundle booleans. Use this for legacy
 *     menu items keyed off specific empId/dept exceptions ("Salvie's
 *     DMS release menu", "Babu's payment view", etc.). The resolver
 *     already encodes the legacy logic; this prop is just the gate.
 *
 * Notes:
 *   • This is defense-in-depth ON TOP OF backend [Authorize(Policy=...)]
 *     attributes. A determined user can edit sessionStorage; backend
 *     rejection is what actually keeps data safe.
 *   • If the user isn't authenticated at all, AuthProtected handles
 *     that upstream; we don't duplicate that check here.
 *   • IT Admin bypasses EVERY gate in this component (role, permission,
 *     module, country, flag). This is the locked behavior per the Phase 1.5
 *     audit (Batch 1 decision #20): "IT Admin should be able to do
 *     anything." The seed SQL grants IT Admin every permission anyway, so
 *     in normal operation IT Admin satisfies permission/module gates on
 *     merit; the bypass is the setup-time safety net that keeps IT Admin
 *     working before the seed runs and matches the canonical "RoleId 4 →
 *     God mode" rule used everywhere else.
 */
const RoleProtected = ({
    // legacy gates (unchanged)
    requireItAdmin = false,
    requireSuperUser = false,
    requireHr = false,
    requireManager = false,
    requireAny = null,         // array of role-name strings

    // new (Phase 3)
    requirePermission = null,  // string — permission name
    requireModule = null,      // string — module key
    requireCountry = null,     // "INDIA" | "USA" | "JOSEPH"
    requireFlag = null,        // string — bundle boolean name

    children
}) => {
    const auth = useAuth();

    // ── IT Admin auto-pass (locked Phase 1.5 decision) ─────────────
    // IT Admin bypasses EVERY gate (role, permission, module, country,
    // flag). The seed grants RoleId 4 all 21 permissions, so under normal
    // operation IT Admin satisfies permission gates on merit; this bypass
    // is the safety net that keeps IT Admin functional before the seed
    // runs (e.g. fresh DB) and matches the "RoleId 4 → unrestricted"
    // rule applied consistently across backend and frontend.
    if (auth.isItAdmin) return <>{children}</>;

    // ── role gates (legacy) ────────────────────────────────────────
    if (requireItAdmin)                          return <Navigate to="/access-denied" replace />;
    if (requireSuperUser && !auth.isSuperUser)   return <Navigate to="/access-denied" replace />;
    if (requireHr        && !auth.isHR)          return <Navigate to="/access-denied" replace />;
    if (requireManager   && !auth.isManager)     return <Navigate to="/access-denied" replace />;

    if (Array.isArray(requireAny) && requireAny.length > 0) {
        const ok = requireAny.some((r) => {
            if (r === 'IT Admin')                return auth.isItAdmin;
            if (r === 'SuperUser')               return auth.isSuperUser;
            if (r === 'HR Admin' || r === 'HR')  return auth.isHR;
            if (r === 'Manager')                 return auth.isManager;
            if (r === 'Finance')                 return auth.isFinance;
            if (r === 'DMS Manager')             return auth.isDmsManager;
            return auth.roleName === r;
        });
        if (!ok) return <Navigate to="/access-denied" replace />;
    }

    // ── permission gate (Phase 3) ──────────────────────────────────
    if (requirePermission && !auth.hasAccess(requirePermission)) {
        return <Navigate to="/access-denied" replace />;
    }

    // ── module gate (Phase 3) ──────────────────────────────────────
    if (requireModule && !auth.canAccessModule(requireModule)) {
        return <Navigate to="/access-denied" replace />;
    }

    // ── country gate (Phase 3) ─────────────────────────────────────
    if (requireCountry && !auth.canViewCountry(requireCountry)) {
        return <Navigate to="/access-denied" replace />;
    }

    // ── arbitrary flag gate (Phase 3) ──────────────────────────────
    if (requireFlag && !auth[requireFlag]) {
        return <Navigate to="/access-denied" replace />;
    }

    return <>{children}</>;
};

export default RoleProtected;
